/**
 * Error classification system.
 * Classifies any thrown error into a standard category for consistent
 * telemetry, user-facing messages, and retry logic.
 */

export type ErrorCategory =
  | "network"
  | "timeout"
  | "parsing"
  | "access"
  | "missing_resource"
  | "unexpected";

export interface ClassifiedError {
  category: ErrorCategory;
  message: string;
  originalMessage: string;
  retryable: boolean;
}

const msg = (e: unknown): string =>
  e instanceof Error ? (e.message || "") : String(e ?? "");

export function classifyError(error: unknown): ClassifiedError {
  const raw = msg(error).toLowerCase();
  const original = msg(error);

  // Network errors
  if (
    raw.includes("failed to fetch") ||
    raw.includes("networkerror") ||
    raw.includes("network request failed") ||
    raw.includes("load failed") ||
    raw.includes("err_internet_disconnected") ||
    raw.includes("err_network")
  ) {
    return { category: "network", message: "network", originalMessage: original, retryable: true };
  }

  // Timeout errors
  if (
    raw.includes("timeout") ||
    raw.includes("timed out") ||
    raw.includes("aborted") ||
    raw.includes("request aborted")
  ) {
    return { category: "timeout", message: "timeout", originalMessage: original, retryable: true };
  }

  // Access / auth errors
  if (
    raw.includes("401") ||
    raw.includes("403") ||
    raw.includes("unauthorized") ||
    raw.includes("forbidden") ||
    raw.includes("jwt") ||
    raw.includes("not authenticated") ||
    raw.includes("access denied") ||
    raw.includes("permission")
  ) {
    return { category: "access", message: "access", originalMessage: original, retryable: false };
  }

  // Missing resource
  if (
    raw.includes("404") ||
    raw.includes("not found") ||
    raw.includes("no rows") ||
    raw.includes("does not exist") ||
    raw.includes("missing")
  ) {
    return { category: "missing_resource", message: "missing_resource", originalMessage: original, retryable: false };
  }

  // Parsing / data errors
  if (
    raw.includes("json") ||
    raw.includes("parse") ||
    raw.includes("unexpected token") ||
    raw.includes("syntaxerror") ||
    raw.includes("invalid data") ||
    raw.includes("cannot read propert")
  ) {
    return { category: "parsing", message: "parsing", originalMessage: original, retryable: true };
  }

  return { category: "unexpected", message: "unexpected", originalMessage: original, retryable: true };
}

/** Returns true if the error is worth auto-retrying (e.g. transient network) */
export function isRetryable(error: unknown): boolean {
  return classifyError(error).retryable;
}
